import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import ThemeToggle from "./ThemeToggle";
import { resolveInitialTheme, type Theme } from "../../lib/theme";

export function TopHeader({ firstName, lastName, tier }: { firstName: string; lastName: string; tier: string }) {
  const initials = ((firstName?.[0] ?? "") + (lastName?.[0] ?? "")).toUpperCase() || "Y";
  const [theme, setTheme] = useState<Theme>(() => resolveInitialTheme());

  // Listen for theme changes (driven by the ThemeToggle) so the logo swaps
  // without needing a full re-render of the dashboard tree.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const t = (document.documentElement.getAttribute("data-theme") as Theme) || "dark";
      setTheme(t);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  // Dark teal logo on transparent → readable on the cream light backdrop.
  // Light celeste logo on transparent → readable on the near-black dark backdrop.
  const logoSrc = theme === "light" ? "/assets/yvree-logo-dark.png" : "/assets/yvree-logo-light.png";
  const logoSrc2x = theme === "light" ? "/assets/yvree-logo-dark@2x.png" : "/assets/yvree-logo-light@2x.png";

  return (
    <div className="grid grid-cols-[140px_1fr] h-[68px] border-b border-d_border/20 bg-d_sf items-center px-6 gap-5">
      <div className="flex items-center">
        <img
          src={logoSrc}
          srcSet={`${logoSrc} 1x, ${logoSrc2x} 2x`}
          alt="Yvree"
          className="h-[32px] w-auto select-none"
          draggable={false}
        />
      </div>
      <div className="flex items-center gap-4">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-bold border border-d_accent/30 text-white"
          style={{ background: "linear-gradient(135deg,#2A9D8F,#264653)" }}
        >
          {initials}
        </div>
        <div>
          <div className="font-serif text-xl font-semibold leading-none">Hello, <span className="italic">{firstName || "friend"}</span></div>
          <div className="text-[9px] text-d_accent tracking-[2px] uppercase mt-1 font-semibold">LifeScribe™</div>
        </div>
        <div className="flex-1" />
        <span
          className="text-[10px] font-bold tracking-[1.6px] uppercase px-3 py-1.5 rounded-full text-white"
          style={{ background: "linear-gradient(135deg,#E76F51,#c44a38)" }}
        >
          {tier}
        </span>
        <ThemeToggle className="ml-2" />
        <button
          onClick={() => supabase.auth.signOut()}
          className="text-d_text2 hover:text-d_text text-[10px] font-medium tracking-[0.10em] uppercase ml-2 px-3 py-2 rounded border border-d_border/30 hover:border-d_accent/50 transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
